import React, { useState } from 'react';
import { SdfBox, SdfSwitch, SdfBadge } from '@synerg/react-components';

const ProjectWebhookDetails = ({ 
  devWebhookConfig,
  prodWebhookConfig,
  devWebhookError,
  prodWebhookError
}) => {
  // DEV reveal toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);

  // PROD reveal toggles
  const [showProdPassword, setShowProdPassword] = useState(false);
  const [showProdApiKey, setShowProdApiKey] = useState(false);
  const [showProdClientSecret, setShowProdClientSecret] = useState(false);

  // Check if we have at least one config
  if (!devWebhookConfig && !prodWebhookConfig) {
    return <div>No webhook configuration found.</div>;
  }

  const renderMaskedValue = () => {
    return (
      <>
        {[...Array(15).keys()].map((i) => (
          <span 
            key={i} 
            className="rounded-full bg-action-default-300 inline-block mr-2" 
            style={{ height: "12px", width: "12px" }}
          ></span>
        ))}
      </>
    );
  };

  const getAuthMethodDisplay = (code) => {
    const authMethodMap = {
      'OAUTH_2.0': 'OAuth 2.0',
      'BASIC_AUTH': 'Basic',
      'WEB_TOKEN': 'API key'
    };
    return authMethodMap[code] || code;
  };

  // Helper function to render a single webhook config box
  const renderWebhookBox = (config, environment, showStates) => {
    if (!config) return null;
    
    const {
      webhookURL,
      authTypeCode,
      basicAuth = {},
      webToken = {},
      oAuthCredentials = {},
      oAuthURL,
      webhookStatusCode = {},
    } = config;
    
    const { showPassword, setShowPassword, showApiKey, setShowApiKey, showClientSecret, setShowClientSecret } = showStates;

    return (
      <div className="mt-5">
        <div className="text-heading-05 font-bold mb-4">
          {environment} Data Connector Webhook
        </div>
        <SdfBox variant="filled" spacing="default" className="mb-6">
          {/* Webhook URL */}
          <div className="flex justify-between items-start mb-4 mt-4">
            <div className="font-bold flex items-center" style={{ minWidth: '200px' }}>
              Webhook host and endpoint
              <SdfBadge
                status={webhookStatusCode.codeValue === "ACTIVE" ? "complete" : "changed"}
                className="ml-2"
              >
                {webhookStatusCode.codeValue === "ACTIVE" ? "Active" : "Inactive"}
              </SdfBadge>
            </div>
            <div className="text-body-02 break-all text-right flex-1 ml-6">{webhookURL}</div>
          </div>

          {/* Auth Method */}
          <div className="flex justify-between items-start mb-4">
            <div className="font-bold" style={{ minWidth: '200px' }}>Authentication method</div>
            <div className="text-body-02 text-right flex-1 ml-6">{getAuthMethodDisplay(authTypeCode)}</div>
          </div>

          {/* BASIC_AUTH fields */}
          {authTypeCode === 'BASIC_AUTH' && (
            <>
              <div className="flex justify-between items-start mb-4">
                <div className="font-bold" style={{ minWidth: '200px' }}>User name</div>
                <div className="text-body-02 text-right flex-1 ml-6">{basicAuth.userID}</div>
              </div>
              <div className="flex justify-between items-start mb-4">
                <div className="font-bold" style={{ minWidth: '200px' }}>Password</div>
                <div className="flex items-center justify-end flex-1 ml-6">
                  <SdfSwitch
                    checked={showPassword}
                    onSdfChange={(event) => setShowPassword(event.detail)}
                  />
                  <span className="text-md text-action-primary font-medium ml-2 mr-4">Reveal</span>
                  <span className="text-body-02">
                    {showPassword ? basicAuth.password : renderMaskedValue()}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* WEB_TOKEN fields */}
          {authTypeCode === 'WEB_TOKEN' && (
            <div className="flex justify-between items-start mb-4">
              <div className="font-bold" style={{ minWidth: '200px' }}>API key</div>
              <div className="flex items-center justify-end flex-1 ml-6">
                <SdfSwitch
                  checked={showApiKey}
                  onSdfChange={(event) => setShowApiKey(event.detail)}
                />
                <span className="text-md text-action-primary font-medium ml-2 mr-4">Reveal</span>
                <span className="text-body-02">
                  {showApiKey ? webToken.accessToken : renderMaskedValue()}
                </span>
              </div>
            </div>
          )}

          {/* OAUTH_2.0 fields */}
          {authTypeCode === 'OAUTH_2.0' && (
            <>
              <div className="flex justify-between items-start mb-4">
                <div className="font-bold" style={{ minWidth: '200px' }}>Client ID</div>
                <div className="text-body-02 text-right flex-1 ml-6">{oAuthCredentials.clientID}</div>
              </div>
              <div className="flex justify-between items-start mb-4">
                <div className="font-bold" style={{ minWidth: '200px' }}>Client Secret</div>
                <div className="flex items-center justify-end flex-1 ml-6">
                  <SdfSwitch
                    checked={showClientSecret}
                    onSdfChange={(event) => setShowClientSecret(event.detail)}
                  />
                  <span className="text-md text-action-primary font-medium ml-2 mr-4">Reveal</span>
                  <span className="text-body-02">
                    {showClientSecret ? oAuthCredentials.clientSecret : renderMaskedValue()}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-start mb-4">
                <div className="font-bold" style={{ minWidth: '200px' }}>Access token URL</div>
                <div className="text-body-02 break-all text-right flex-1 ml-6">{oAuthURL}</div>
              </div>
              <div className="flex justify-between items-start mb-4">
                <div className="font-bold" style={{ minWidth: '200px' }}>OAuth scopes</div>
                <div className="text-body-02 text-right flex-1 ml-6 overflow-hidden">
                  {oAuthCredentials.scope
                    ? oAuthCredentials.scope
                    .split(' ')
                    .filter(Boolean)
                    .map((scope, idx) => (
                      <span
                        key={idx}
                        style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
                      >
                        {scope.replace(/\//g, '/\u200B')}
                        <br />
                      </span>
                    ))
                    : null}
                </div>
              </div>
            </>
          )}
        </SdfBox>
      </div>
    );
  };

  return (
    <div className="block w-full">
      {/* DEV Webhook Config */}
      {devWebhookConfig && renderWebhookBox(
        devWebhookConfig, 
        'Development',
        { showPassword, setShowPassword, showApiKey, setShowApiKey, showClientSecret, setShowClientSecret }
      )}
      
      {devWebhookError && !devWebhookConfig && (
        <div className="text-danger-01 mb-4">{devWebhookError}</div>
      )}
      
      {/* PROD Webhook Config */}
      {prodWebhookConfig && renderWebhookBox(
        prodWebhookConfig,
        'Production',
        { 
          showPassword: showProdPassword, 
          setShowPassword: setShowProdPassword, 
          showApiKey: showProdApiKey, 
          setShowApiKey: setShowProdApiKey, 
          showClientSecret: showProdClientSecret, 
          setShowClientSecret: setShowProdClientSecret 
        }
      )}
      
      {prodWebhookError && !prodWebhookConfig && (
        <div className="text-danger-01 mb-4">{prodWebhookError}</div>
      )}
    </div>
  );
};

export default ProjectWebhookDetails;